import { Suspense, lazy } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { useAuth } from '@/features/auth/AuthContext';
import { LoadingState } from '@/components/ui';
import { AppShell } from '@/components/layout/AppShell';
import { ProtectedRoute } from '@/app/ProtectedRoute';
import { MANAGERS, OBSERVERS, STAFF, SUPER } from '@/features/auth/access';
import { LoginPage } from '@/pages/LoginPage';
import { ForgotPasswordPage } from '@/pages/ForgotPasswordPage';
import { ResetPasswordPage } from '@/pages/ResetPasswordPage';
import { ChangePasswordPage } from '@/pages/ChangePasswordPage';
import { DashboardPage } from '@/pages/DashboardPage';
import { LearnPage } from '@/pages/LearnPage';
import { SessionPage } from '@/pages/SessionPage';
import { QuizPage } from '@/pages/QuizPage';
import { NotFoundPage } from '@/pages/NotFoundPage';

/**
 * Pages secondaires de l'espace apprenant, chargees a la demande : le premier
 * chargement ne porte que le parcours de formation lui-meme.
 * Secondary learner pages, loaded on demand.
 */
const CertificatesPage = lazy(() => import('@/pages/CertificatesPage').then((module) => ({ default: module.CertificatesPage })));
const VerifyCertificatePage = lazy(() => import('@/pages/VerifyCertificatePage').then((module) => ({ default: module.VerifyCertificatePage })));
const NotificationsPage = lazy(() => import('@/pages/NotificationsPage').then((module) => ({ default: module.NotificationsPage })));
const AnnouncementsPage = lazy(() => import('@/pages/AnnouncementsPage').then((module) => ({ default: module.AnnouncementsPage })));
const ProfilePage = lazy(() => import('@/pages/ProfilePage').then((module) => ({ default: module.ProfilePage })));
const HelpPage = lazy(() => import('@/pages/HelpPage').then((module) => ({ default: module.HelpPage })));
const AccessibilityPage = lazy(() => import('@/pages/AccessibilityPage').then((module) => ({ default: module.AccessibilityPage })));
const AssignmentsPage = lazy(() => import('@/pages/AssignmentsPage').then((module) => ({ default: module.AssignmentsPage })));
const ForumPage = lazy(() => import('@/pages/ForumPage').then((module) => ({ default: module.ForumPage })));
const ForumThreadPage = lazy(() => import('@/pages/ForumThreadPage').then((module) => ({ default: module.ForumThreadPage })));
const MessagesPage = lazy(() => import('@/pages/MessagesPage').then((module) => ({ default: module.MessagesPage })));
const SkillsPage = lazy(() => import('@/pages/SkillsPage').then((module) => ({ default: module.SkillsPage })));
const RevisionPage = lazy(() => import('@/pages/RevisionPage').then((module) => ({ default: module.RevisionPage })));
const ClassroomsPage = lazy(() => import('@/pages/ClassroomsPage').then((module) => ({ default: module.ClassroomsPage })));
const ClassroomRoomPage = lazy(() =>
  import('@/pages/ClassroomRoomPage').then((module) => ({ default: module.ClassroomRoomPage })),
);
const ScormPlayerPage = lazy(() => import('@/pages/ScormPlayerPage').then((module) => ({ default: module.ScormPlayerPage })));
const LtiLaunchPage = lazy(() => import('@/pages/LtiLaunchPage').then((module) => ({ default: module.LtiLaunchPage })));

/**
 * Le panel d'administration est charge a la demande : les 2 000 agents
 * apprenants n'en telechargent jamais le code.
 * The admin panel is loaded on demand.
 */
const AnalyticsPage = lazy(() => import('@/pages/admin/AnalyticsPage').then((module) => ({ default: module.AnalyticsPage })));
const UsersPage = lazy(() => import('@/pages/admin/UsersPage').then((module) => ({ default: module.UsersPage })));
const CohortsPage = lazy(() => import('@/pages/admin/CohortsPage').then((module) => ({ default: module.CohortsPage })));
const AgentJourneyPage = lazy(() => import('@/pages/admin/AgentJourneyPage').then((module) => ({ default: module.AgentJourneyPage })));
const ContentPage = lazy(() => import('@/pages/admin/ContentPage').then((module) => ({ default: module.ContentPage })));
const FilesPage = lazy(() => import('@/pages/admin/FilesPage').then((module) => ({ default: module.FilesPage })));
const GradingPage = lazy(() => import('@/pages/admin/GradingPage').then((module) => ({ default: module.GradingPage })));
const AdminAssignmentsPage = lazy(() => import('@/pages/admin/AssignmentsPage').then((module) => ({ default: module.AdminAssignmentsPage })));
const CompetenciesPage = lazy(() => import('@/pages/admin/CompetenciesPage').then((module) => ({ default: module.CompetenciesPage })));
const ReportsPage = lazy(() => import('@/pages/admin/ReportsPage').then((module) => ({ default: module.ReportsPage })));
const AuditPage = lazy(() => import('@/pages/admin/AuditPage').then((module) => ({ default: module.AuditPage })));
const ScormPage = lazy(() => import('@/pages/admin/ScormPage').then((module) => ({ default: module.ScormPage })));
const LtiPage = lazy(() => import('@/pages/admin/LtiPage').then((module) => ({ default: module.LtiPage })));
const SettingsPage = lazy(() => import('@/pages/admin/SettingsPage').then((module) => ({ default: module.SettingsPage })));

// Les ensembles de roles vivent avec la navigation : une entree de menu et la
// garde de sa route ne peuvent pas diverger si elles lisent la meme liste.


/**
 * Le centre d'aide s'affiche dans la coque applicative pour un agent connecte,
 * et en page autonome pour un visiteur non authentifie.
 */
function HelpRoute() {
  const { isAuthenticated, initializing } = useAuth();

  if (initializing) return <LoadingState className="min-h-screen" />;

  return isAuthenticated ? (
    <AppShell>
      <HelpPage />
    </AppShell>
  ) : (
    <HelpPage />
  );
}

/** Meme logique que le centre d'aide : coque applicative si connecte. */
function AccessibilityRoute() {
  const { isAuthenticated, initializing } = useAuth();

  if (initializing) return <LoadingState className="min-h-screen" />;

  return isAuthenticated ? (
    <AppShell>
      <AccessibilityPage />
    </AppShell>
  ) : (
    <AccessibilityPage />
  );
}

export function AppRouter() {
  return (
    <Suspense fallback={<LoadingState className="min-h-screen" />}>
      <Routes>
      {/* Routes publiques */}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/verify" element={<VerifyCertificatePage />} />
      {/* Arrivee d'un lancement LTI : la session est ouverte avant tout rendu. */}
      <Route path="/lti/launch" element={<LtiLaunchPage />} />
      <Route path="/verify/:code" element={<VerifyCertificatePage />} />
      {/* Le centre d'aide reste joignable sans session : un agent qui n'arrive
          pas a se connecter est precisement celui qui en a besoin. */}
      <Route path="/help" element={<HelpRoute />} />
      {/* La declaration d'accessibilite reste publique, comme le centre d'aide. */}
      <Route path="/accessibility" element={<AccessibilityRoute />} />

      <Route
        path="/change-password"
        element={
          <ProtectedRoute>
            <ChangePasswordPage />
          </ProtectedRoute>
        }
      />

      {/* Application authentifiee */}
      <Route
        element={
          <ProtectedRoute>
            <AppShell />
          </ProtectedRoute>
        }
      >
        <Route index element={<Navigate to="/dashboard" replace />} />
        <Route path="/dashboard" element={<DashboardPage />} />
        <Route path="/learn" element={<LearnPage />} />
        <Route path="/learn/sessions/:sessionId" element={<SessionPage />} />
        <Route path="/learn/scorm/:packageId" element={<ScormPlayerPage />} />
        <Route path="/quizzes/:quizId" element={<QuizPage />} />
        <Route path="/assignments" element={<AssignmentsPage />} />
        <Route path="/forum" element={<ForumPage />} />
        <Route path="/forum/:threadId" element={<ForumThreadPage />} />
        <Route path="/messages" element={<MessagesPage />} />
        <Route path="/revision" element={<RevisionPage />} />
        <Route path="/classrooms" element={<ClassroomsPage />} />
        <Route path="/classrooms/:classroomId/room" element={<ClassroomRoomPage />} />
        <Route path="/skills" element={<SkillsPage />} />
        <Route path="/certificates" element={<CertificatesPage />} />
        <Route path="/notifications" element={<NotificationsPage />} />
        <Route path="/announcements" element={<AnnouncementsPage />} />
        <Route path="/profile" element={<ProfilePage />} />

        {/* Panel d'administration, charge a la demande */}
        <Route
          path="/admin"
          element={
            <ProtectedRoute roles={OBSERVERS}>
              <Suspense fallback={<LoadingState />}>
                <AnalyticsPage />
              </Suspense>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/users"
          element={
            <ProtectedRoute roles={OBSERVERS}>
              <Suspense fallback={<LoadingState />}>
                <UsersPage />
              </Suspense>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/users/:userId"
          element={
            <ProtectedRoute roles={OBSERVERS}>
              <Suspense fallback={<LoadingState />}>
                <AgentJourneyPage />
              </Suspense>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/cohorts"
          element={
            <ProtectedRoute roles={OBSERVERS}>
              <Suspense fallback={<LoadingState />}>
                <CohortsPage />
              </Suspense>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/content"
          element={
            <ProtectedRoute roles={OBSERVERS}>
              <Suspense fallback={<LoadingState />}>
                <ContentPage />
              </Suspense>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/files"
          element={
            <ProtectedRoute roles={STAFF}>
              <Suspense fallback={<LoadingState />}>
                <FilesPage />
              </Suspense>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/assignments"
          element={
            <ProtectedRoute roles={OBSERVERS}>
              <Suspense fallback={<LoadingState />}>
                <AdminAssignmentsPage />
              </Suspense>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/competencies"
          element={
            <ProtectedRoute roles={OBSERVERS}>
              <Suspense fallback={<LoadingState />}>
                <CompetenciesPage />
              </Suspense>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/grading"
          element={
            <ProtectedRoute roles={STAFF}>
              <Suspense fallback={<LoadingState />}>
                <GradingPage />
              </Suspense>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/reports"
          element={
            <ProtectedRoute roles={OBSERVERS}>
              <Suspense fallback={<LoadingState />}>
                <ReportsPage />
              </Suspense>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/audit"
          element={
            <ProtectedRoute roles={MANAGERS}>
              <Suspense fallback={<LoadingState />}>
                <AuditPage />
              </Suspense>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/scorm"
          element={
            <ProtectedRoute roles={OBSERVERS}>
              <Suspense fallback={<LoadingState />}>
                <ScormPage />
              </Suspense>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/lti"
          element={
            <ProtectedRoute roles={SUPER}>
              <Suspense fallback={<LoadingState />}>
                <LtiPage />
              </Suspense>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/settings"
          element={
            <ProtectedRoute roles={MANAGERS}>
              <Suspense fallback={<LoadingState />}>
                <SettingsPage />
              </Suspense>
            </ProtectedRoute>
          }
        />
      </Route>

        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </Suspense>
  );
}
